// ZATCA Report — the signed-in branch's sales invoices and returns and where each
// one stands with ZATCA (API ZatcaController, DB script section 32): not signed
// yet, signed and waiting, reported / cleared, rejected, or not delivered.
// "Send" signs and sends one invoice now; "Send all pending" does the whole branch.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import LoadingUtils from "../../core/utils/LoadingUtils";
import {
  downloadZatcaXml,
  getZatcaReport,
  resubmitZatca,
  zatcaError,
} from "../../services/ZatcaApiServices";
import { amt, Empty, PageHead, Stat } from "../../components/transactions/pos/module/PosParts";
import "../../style/css/pos-module.css";

const STATUSES = [
  { value: "ALL", label: "All" },
  { value: "PENDING", label: "Waiting to send" },
  { value: "UNSIGNED", label: "Not signed yet" },
  { value: "REPORTED", label: "Reported" },
  { value: "CLEARED", label: "Cleared" },
  { value: "REJECTED", label: "Rejected" },
  { value: "SEND_FAILED", label: "Not delivered" },
];

const STATUS_VIEW = {
  UNSIGNED: { label: "Not signed", tone: "" },
  SIGNED: { label: "Waiting", tone: "pm-pill--warn" },
  SEND_FAILED: { label: "Not delivered", tone: "pm-pill--warn" },
  REPORTED: { label: "Reported", tone: "pm-pill--good" },
  CLEARED: { label: "Cleared", tone: "pm-pill--good" },
  REJECTED: { label: "Rejected", tone: "pm-pill--bad" },
};

const DOC_VIEW = {
  SIMSI: "Simplified invoice",
  STDSI: "Standard invoice",
  SIMCN: "Simplified credit note",
  STDCN: "Standard credit note",
};

const ZatcaReport = () => {
  const { branch } = useSelector((state) => state.branchlist);
  const [from, setFrom] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DD"));
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [report, setReport] = useState({ rows: [] });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!branch?.guid || !from || !to) return;
    setLoading(true);
    try {
      setReport(await getZatcaReport(branch.guid, from, to, status));
    } catch (error) {
      toast.error(zatcaError(error, "Could not load the ZATCA report"));
      setReport({ rows: [] });
    } finally {
      setLoading(false);
    }
  }, [branch, from, to, status]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = report?.rows || [];
    return q
      ? list.filter(
          (r) =>
            `${r.entryNumber}`.toLowerCase().includes(q) ||
            `${r.customerName ?? ""}`.toLowerCase().includes(q) ||
            `${r.responseMsg ?? ""}`.toLowerCase().includes(q)
        )
      : list;
  }, [report, search]);

  const send = async (salesGuIDs) => {
    setSending(true);
    try {
      const r = await resubmitZatca(branch.guid, salesGuIDs);
      const parts = [];
      if (r.accepted) parts.push(`${r.accepted} accepted`);
      if (r.rejected) parts.push(`${r.rejected} rejected`);
      if (r.failed) parts.push(`${r.failed} not done`);
      const text = parts.length ? parts.join(", ") : r.sent ? `${r.sent} sent` : "Nothing was due";
      if (r.rejected || r.failed) toast.error(`${text}. ${(r.messages || []).slice(0, 2).join(" · ")}`, { duration: 8000 });
      else toast.success(text);
      await load();
    } catch (error) {
      toast.error(zatcaError(error, "Could not send to ZATCA"));
    } finally {
      setSending(false);
    }
  };

  const download = async (r) => {
    const name = `${`${r.entryNumber || "invoice"}`.replace(/[^A-Za-z0-9_-]/g, "_")}_${r.documentType || ""}_ICV${r.icv ?? ""}.xml`;
    try {
      await downloadZatcaXml(r.salesGuID, name);
    } catch (error) {
      toast.error("Could not download the XML");
    }
  };

  const canSend = (r) => ["UNSIGNED", "SIGNED", "SEND_FAILED"].includes(r.status);

  return (
    <div className="page-wrapper">
      <div className="content pm">
        <LoadingUtils loading={sending} />
        <PageHead title="ZATCA Report" subtitle={`E-invoices of ${branch?.name || "this branch"} and their ZATCA status`}>
          <div className="pm-search">
            <input
              type="date"
              className="form-control"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From"
            />
            <input
              type="date"
              className="form-control"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To"
            />
            <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <input
              className="form-control"
              placeholder="Invoice no, customer, message"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="button" className="btn btn-pm" disabled={sending || !branch?.guid} onClick={() => send([])}>
              Send all pending
            </button>
          </div>
        </PageHead>

        <div className="pm-stats">
          <Stat label={`Signed in ${dayjs(to).year()}`} value={report?.yearTotal ?? 0} hero />
          <Stat label="Accepted by ZATCA" value={report?.yearDone ?? 0} />
          <Stat label="Waiting to send" value={report?.yearPending ?? 0} />
          <Stat label="Rejected" value={report?.yearRejected ?? 0} />
        </div>

        <div className="pm-card">
          {loading ? (
            <Empty>Loading…</Empty>
          ) : rows.length ? (
            <div className="pm-table-wrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>No</th>
                    <th>Type</th>
                    <th className="num">ICV</th>
                    <th>Customer</th>
                    <th className="num">Net</th>
                    <th className="num">VAT</th>
                    <th>Status</th>
                    <th>ZATCA message</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const view = STATUS_VIEW[r.status] || { label: r.status, tone: "" };
                    return (
                      <tr key={r.salesGuID}>
                        <td>{r.invoiceDate ? dayjs(r.invoiceDate).format("DD MMM YYYY, hh:mm A") : "—"}</td>
                        <td>
                          <b>{r.entryNumber}</b>
                        </td>
                        <td>
                          {r.documentType
                            ? DOC_VIEW[r.documentType] || r.documentType
                            : r.transType === "SR"
                            ? "Return"
                            : "Invoice"}
                        </td>
                        <td className="num">{r.icv ?? "—"}</td>
                        <td>{r.customerName || "—"}</td>
                        <td className="num">{amt(r.netTotal)}</td>
                        <td className="num">{amt(r.taxTotal)}</td>
                        <td>
                          <span className={`pm-pill ${view.tone}`.trim()}>{view.label}</span>
                          {r.attempts > 1 ? <small> · {r.attempts} tries</small> : null}
                        </td>
                        <td title={r.responseMsg || ""} style={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {r.responseMsg || "—"}
                        </td>
                        <td className="num" style={{ whiteSpace: "nowrap" }}>
                          {canSend(r) ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-pm"
                              disabled={sending}
                              onClick={() => send([r.salesGuID])}
                            >
                              Send
                            </button>
                          ) : null}{" "}
                          {r.id ? (
                            <button type="button" className="btn btn-sm btn-light" onClick={() => download(r)}>
                              XML
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty>
              No invoices match. Invoices appear here once ZATCA is on for the branch (ADMINISTRATION › ZATCA Setup) and
              they are dated from its start date.
            </Empty>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZatcaReport;
